import type { StoredSectorTemplate, SectorTemplateStore, OrgSector } from '../api/keycloak-admin/org-config';
import { fetchTemplateStore, saveTemplateStore } from '../api/keycloak-admin';
import { getSeedTemplates } from '../data/seed-templates';

/**
 * Load templates from Keycloak, seeding built-ins on first access.
 * On subsequent loads, merges any new built-in templates from code updates.
 */
export async function loadTemplates(token: string): Promise<StoredSectorTemplate[]> {
  const existing = await fetchTemplateStore(token);

  if (!existing) {
    const seeds = getSeedTemplates();
    const store: SectorTemplateStore = {
      version: 1,
      seededAt: new Date().toISOString(),
      templates: seeds,
    };
    await saveTemplateStore(token, store);
    return store.templates;
  }

  const merged = mergeNewSeeds(existing);
  if (merged.changed) {
    await saveTemplateStore(token, merged.store);
  }
  return merged.store.templates;
}

/** Filter templates by sector */
export function filterBySector(
  templates: StoredSectorTemplate[],
  sector: OrgSector,
): StoredSectorTemplate[] {
  return templates.filter((t) => t.sector === sector);
}

/** Find a specific template by sector and type name */
export function findTemplate(
  templates: StoredSectorTemplate[],
  sector: OrgSector,
  typeName: string,
): StoredSectorTemplate | undefined {
  return templates.find((t) => t.sector === sector && t.typeName === typeName);
}

/** Create a new custom template and persist to Keycloak */
export async function createTemplate(
  token: string,
  data: Omit<StoredSectorTemplate, 'id' | 'builtIn' | 'createdAt' | 'updatedAt'>,
): Promise<StoredSectorTemplate> {
  const store = await fetchTemplateStore(token);
  if (!store) throw new Error('Template store not initialized');

  const now = new Date().toISOString();
  const template: StoredSectorTemplate = {
    ...data,
    id: crypto.randomUUID(),
    builtIn: false,
    createdAt: now,
    updatedAt: now,
  };
  store.templates.push(template);
  await saveTemplateStore(token, store);
  return template;
}

/** Delete a custom template (throws if builtIn) */
export async function deleteTemplate(token: string, templateId: string): Promise<void> {
  const store = await fetchTemplateStore(token);
  if (!store) throw new Error('Template store not initialized');

  const idx = store.templates.findIndex((t) => t.id === templateId);
  if (idx === -1) throw new Error('Template not found');
  if (store.templates[idx].builtIn) throw new Error('Cannot delete built-in template');

  store.templates.splice(idx, 1);
  await saveTemplateStore(token, store);
}

/** Check for new seed templates and merge them into the existing store */
function mergeNewSeeds(
  existing: SectorTemplateStore,
): { store: SectorTemplateStore; changed: boolean } {
  const seeds = getSeedTemplates();
  const existingBuiltInNames = new Set(
    existing.templates
      .filter((t) => t.builtIn)
      .map((t) => `${t.sector}::${t.typeName}`),
  );

  const newTemplates = seeds.filter(
    (s) => !existingBuiltInNames.has(`${s.sector}::${s.typeName}`),
  );

  if (newTemplates.length === 0) {
    return { store: existing, changed: false };
  }

  return {
    store: {
      ...existing,
      templates: [...existing.templates, ...newTemplates],
    },
    changed: true,
  };
}
